/**
 * 多语言源码扫描工具
 * 支持 Java/Node/Python/Go/Rust 等语言的源码收集和分类
 */

// 跳过分析的目录
const SKIP_DIRS = new Set([
  'node_modules', 'target', '.git', '.idea', 'dist', 'build', '.mvn',
  '.gradle', '__pycache__', '.next', '.nuget', 'bin', 'obj',
  '.cache', 'coverage', '.husky', '.github',
]);

// 跳过分析的文件扩展名（二进制/媒体文件）
const SKIP_EXTENSIONS = new Set([
  '.class', '.jar', '.war', '.ear', '.png', '.jpg', '.jpeg', '.gif',
  '.svg', '.ico', '.woff', '.woff2', '.eot', '.ttf', '.otf',
  '.mp4', '.mp3', '.avi', '.mov', '.pdf', '.doc', '.docx',
  '.xls', '.xlsx', '.ppt', '.pptx', '.zip', '.tar', '.gz',
  '.7z', '.rar', '.exe', '.dll', '.so', '.dylib', '.o', '.a',
  '.lib', '.obj', '.pyc', '.pyo', '.DS_Store',
]);

// ============================================================
// 1. 各语言分类规则
// ============================================================
const CLASSIFIERS = {
  JAVA: {
    extensions: ['.java'],
    classify(fileName, content) {
      const simpleName = fileName.replace('.java', '');
      if (content.includes(' enum ')) return 'ENUM';
      if (content.includes(' @TableName') || content.includes('@TableName(')) return 'ENTITY';
      if (fileName.endsWith('Controller.java')) return 'CONTROLLER';
      if (fileName.endsWith('ServiceImpl.java')) return 'SERVICE_IMPL';
      if (fileName.endsWith('Service.java')) return 'SERVICE';
      if (fileName.endsWith('Mapper.java')) return 'MAPPER';
      if (fileName.endsWith('Vo.java') || fileName.endsWith('VO.java')) return 'DTO';
      if (fileName.endsWith('Dto.java') || fileName.endsWith('DTO.java')) return 'DTO';
      if (fileName.endsWith('Config.java') || fileName.endsWith('Configuration.java')) return 'CONFIG';
      if (fileName.endsWith('Converter.java') || fileName.endsWith('Convert.java')) return 'UTIL';
      if (fileName.endsWith('Util.java') || fileName.endsWith('Utils.java')) return 'UTIL';
      if (content.includes(' interface ')) return 'INTERFACE';
      return null; // 让 AI 补充判断
    },
    getCode(fileName, type, content) {
      const name = fileName.replace('.java', '');
      switch (type) {
        case 'ENTITY': {
          const tn = content.match(/@TableName\("([^"]+)"\)/);
          return tn ? tn[1] : name.replace('Entity', '');
        }
        case 'CONTROLLER': {
          const apiMatch = content.match(/@RequestMapping\("?([^")]+)"?\)/);
          if (apiMatch) return apiMatch[1].replace(/^\//, '').split('/')[0];
          return name.replace('Controller', '');
        }
        case 'ENUM': {
          const ec = content.match(/@EnumDict\s*\(\s*enumCode\s*=\s*"([^"]+)"/);
          return ec ? ec[1] : name.replace('Enum', '');
        }
        case 'SERVICE':
        case 'SERVICE_IMPL':
          return name.replace('Service', '').replace('Impl', '');
        case 'MAPPER':
          return name.replace('Mapper', '');
        default:
          return name;
      }
    },
    aiPromptType: 'Java',
  },
  NODE: {
    extensions: ['.js', '.jsx', '.ts', '.tsx', '.vue'],
    classify(fileName, content) {
      const ext = fileName.substring(fileName.lastIndexOf('.'));
      const name = fileName.replace(ext, '');
      const lc = name.toLowerCase();

      if (ext === '.vue') return 'PAGE';
      if (lc.endsWith('.store') || lc.endsWith('store') || lc.endsWith('pinia')) return 'STORE';
      if (lc.endsWith('.api') || lc.endsWith('service') || lc.endsWith('request')) return 'API';
      if (lc.endsWith('.router') || lc.endsWith('route') || lc.endsWith('routes')) return 'ROUTER';
      if (lc.endsWith('.config') || lc.endsWith('config') || lc.endsWith('env')) return 'CONFIG';
      if (lc.endsWith('.util') || lc.endsWith('util') || lc.endsWith('helper')) return 'UTIL';
      if (lc.endsWith('.component') || lc.endsWith('component')) return 'COMPONENT';
      if (fileName === 'main.js' || fileName === 'main.ts' || fileName === 'index.js' || fileName === 'index.ts' || fileName === 'app.js' || fileName === 'app.ts') return 'PAGE';
      if (lc.includes('layout')) return 'LAYOUT';
      if (lc.includes('directive')) return 'DIRECTIVE';
      if (lc.includes('mixin') || lc.includes('composable') || lc.includes('hook')) return 'MIXIN';
      return null;
    },
    getCode(fileName, type, content) {
      const ext = fileName.substring(fileName.lastIndexOf('.'));
      return fileName.replace(ext, '');
    },
    aiPromptType: 'JavaScript/TypeScript',
  },
  PYTHON: {
    extensions: ['.py', '.pyw'],
    classify(fileName, content) {
      const name = fileName.replace('.py', '');
      const lc = name.toLowerCase();

      if (lc.endsWith('model') || lc.endsWith('models') || content.includes('class.*Model') || content.includes('db.Model') || content.includes('models.Model')) return 'MODEL';
      if (lc.endsWith('view') || lc.endsWith('views')) return 'VIEW';
      if (lc.endsWith('controller') || lc.endsWith('api') || lc.endsWith('resource')) return 'CONTROLLER';
      if (lc.endsWith('service') || lc.endsWith('services')) return 'SERVICE';
      if (lc.endsWith('config') || lc.endsWith('settings') || lc.endsWith('configuration')) return 'CONFIG';
      if (lc.endsWith('form') || lc.endsWith('forms')) return 'FORM';
      if (lc.endsWith('serializer') || lc.endsWith('schema')) return 'DTO';
      if (lc.endsWith('admin') || lc.endsWith('admin')) return 'ADMIN';
      if (lc.endsWith('util') || lc.endsWith('utils') || lc.endsWith('helper') || lc.endsWith('helpers')) return 'UTIL';
      if (name === 'manage' || name === 'app' || name === 'wsgi' || name === 'asgi') return 'CONFIG';
      if (lc.endsWith('test') || lc.startsWith('test_')) return 'TEST';
      return null;
    },
    getCode(fileName, type) {
      return fileName.replace('.py', '');
    },
    aiPromptType: 'Python',
  },
  GO: {
    extensions: ['.go'],
    classify(fileName, content) {
      const name = fileName.replace('.go', '');
      const lc = name.toLowerCase();

      if (lc.endsWith('handler') || lc.endsWith('api') || lc.endsWith('controller')) return 'CONTROLLER';
      if (lc.endsWith('service') || lc.endsWith('services')) return 'SERVICE';
      if (lc.endsWith('model') || lc.endsWith('models') || lc.endsWith('entity') || lc.endsWith('entities') || content.includes('gorm.Model')) return 'MODEL';
      if (lc.endsWith('repository') || lc.endsWith('repo') || lc.endsWith('dao') || lc.endsWith('store')) return 'REPOSITORY';
      if (lc.endsWith('config') || lc.endsWith('configuration')) return 'CONFIG';
      if (lc.endsWith('middleware') || lc.endsWith('middlewares')) return 'MIDDLEWARE';
      if (lc.endsWith('route') || lc.endsWith('routes') || lc.endsWith('router')) return 'ROUTER';
      if (lc.endsWith('dto') || lc.endsWith('vo') || lc.endsWith('request') || lc.endsWith('response')) return 'DTO';
      if (lc.endsWith('util') || lc.endsWith('utils') || lc.endsWith('helper')) return 'UTIL';
      if (lc.endsWith('test') || lc.endsWith('_test')) return 'TEST';
      if (name === 'main') return 'CONFIG';
      return null;
    },
    getCode(fileName) {
      return fileName.replace('.go', '');
    },
    aiPromptType: 'Go',
  },
  GENERIC: {
    extensions: ['.json', '.yml', '.yaml', '.xml', '.toml', '.cfg', '.ini', '.conf', '.env'],
    classify(fileName) {
      const lc = fileName.toLowerCase();
      if (lc.includes('config') || lc.includes('setting') || lc.includes('application')) return 'CONFIG';
      if (lc === 'dockerfile' || lc.includes('docker')) return 'DOCKER';
      if (lc === 'readme.md') return 'DOC';
      return null;
    },
    getCode(fileName) {
      return fileName;
    },
    aiPromptType: 'Generic',
  },
};

// AI 分类使用的 type 映射
const AI_TYPE_MAP = {
  JAVA: ['ENUM', 'ENTITY', 'CONTROLLER', 'SERVICE', 'SERVICE_IMPL', 'MAPPER', 'CONFIG', 'DTO', 'VO', 'UTIL', 'INTERFACE', 'OTHER'],
  NODE: ['COMPONENT', 'PAGE', 'STORE', 'API', 'CONFIG', 'UTIL', 'LAYOUT', 'ROUTER', 'DIRECTIVE', 'MIXIN', 'STYLE', 'OTHER'],
  PYTHON: ['MODEL', 'VIEW', 'CONTROLLER', 'SERVICE', 'CONFIG', 'FORM', 'DTO', 'ADMIN', 'UTIL', 'TEST', 'OTHER'],
  GO: ['CONTROLLER', 'SERVICE', 'MODEL', 'REPOSITORY', 'CONFIG', 'MIDDLEWARE', 'ROUTER', 'DTO', 'UTIL', 'TEST', 'OTHER'],
  GENERIC: ['CONFIG', 'DOCKER', 'DOC', 'SCRIPT', 'OTHER'],
};

/**
 * 检测项目类型并扫描源码文件
 * @param {FileSystemDirectoryHandle} dirHandle 目录句柄
 * @param {string} projectTypeKey MAVEN | NODE | PYTHON | GO | etc.
 * @returns {Promise<Array>} 模型数组 [{fileName, filePath, type, code, description, ...}]
 */
export async function scanSourceFiles(dirHandle, projectTypeKey = 'MAVEN') {
  const classifier = getClassifier(projectTypeKey);
  const extensions = classifier.extensions;
  const models = [];

  await walkSourceFiles(dirHandle, '', models, extensions);
  return models;
}

/**
 * 遍历并收集指定扩展名的源码文件（带基础规则分类）
 */
async function walkSourceFiles(handle, path, result, extensions) {
  for await (const entry of handle.values()) {
    const entryPath = path ? `${path}/${entry.name}` : entry.name;

    if (entry.kind === 'directory') {
      if (SKIP_DIRS.has(entry.name)) continue;
      await walkSourceFiles(entry, entryPath, result, extensions);
    } else {
      const ext = getFileExtension(entry.name);
      if (SKIP_EXTENSIONS.has(ext)) continue;
      if (extensions.length > 0 && !extensions.includes(ext)) continue;

      try {
        const file = await entry.getFile();
        const content = await file.text();
        if (!content || content.length < 10) continue;

        const model = {
          fileName: entry.name,
          filePath: entryPath,
          simpleName: entry.name.replace(ext, ''),
          type: null,
          code: '',
          description: '',
          content: content, // 原始内容（用于AI分析，不提交后端）
          _originalContent: content,
        };

        result.push(model);
      } catch (e) {
        // 跳过无法读取的文件
      }
    }
  }
}

function getFileExtension(filename) {
  const idx = filename.lastIndexOf('.');
  return idx >= 0 ? filename.substring(idx).toLowerCase() : '';
}

/**
 * 获取项目类型对应的分类器
 */
function getClassifier(projectTypeKey) {
  const map = {
    MAVEN: CLASSIFIERS.JAVA,
    GRADLE: CLASSIFIERS.JAVA,
    NODE: CLASSIFIERS.NODE,
    PYTHON: CLASSIFIERS.PYTHON,
    GO: CLASSIFIERS.GO,
    RUST: CLASSIFIERS.GENERIC,
    DOTNET: CLASSIFIERS.GENERIC,
    GENERIC: CLASSIFIERS.GENERIC,
  };
  return map[projectTypeKey] || CLASSIFIERS.GENERIC;
}

/**
 * 获取分类器配置（用于 UI 展示）
 */
export function getClassifierConfig(projectTypeKey) {
  const classifier = getClassifier(projectTypeKey);
  return {
    extensions: classifier.extensions,
    typeOptions: AI_TYPE_MAP[classifier.aiPromptType] || AI_TYPE_MAP.GENERIC,
    languageLabel: classifier.aiPromptType,
  };
}

/**
 * 对已收集的文件执行规则分类（0 成本快速分类）
 */
export function classifyByRules(models, projectTypeKey) {
  const classifier = getClassifier(projectTypeKey);
  let classifiedCount = 0;

  for (const model of models) {
    const type = classifier.classify(model.fileName, model.content || '');
    if (type) {
      model.type = type;
      model.code = classifier.getCode(model.fileName, type, model.content || '');
      model._classifiedBy = 'RULE';
      classifiedCount++;
    }
  }

  return classifiedCount;
}

/**
 * 对未分类文件构建 AI prompt
 */
export function buildAIClassifyPrompt(unclassified, projectTypeKey, disclosure = 'COMPACT') {
  const classifier = getClassifier(projectTypeKey);
  const typeOptions = AI_TYPE_MAP[classifier.aiPromptType] || AI_TYPE_MAP.GENERIC;

  // 根据披露策略控制内容量
  const fileInfos = unclassified.map((f, idx) => {
    let contentPreview = '';
    if (disclosure === 'FULL' || disclosure === 'PROGRESSIVE') {
      contentPreview = '\n内容: ' + (f.content || '').substring(0, 800);
    } else if (disclosure === 'COMPACT') {
      contentPreview = '\n内容摘要: ' + (f.content || '').substring(0, 200);
    }
    return `[${idx}] ${f.filePath}${contentPreview}`;
  }).join('\n\n');

  return `你是一个源码分类专家。请对以下 ${classifier.aiPromptType} 源码文件进行分类。

每个文件必须从以下类型中选择一个：
${typeOptions.join(', ')}

## 文件列表
${fileInfos}

## 返回格式
纯 JSON 数组，不要 markdown 代码块：
[
  {"index": 0, "type": "类型", "code": "业务编码（从文件名推断）", "description": "文件功能描述"}
]

注意：
- 根据文件内容准确判断类型，不确定则用 OTHER
- code 字段从文件名或内容推断业务编码（如 UserService → user, OrderController → order）
- description 用一句话说明文件用途`;
}

/**
 * 解析 AI 分类结果
 */
export function parseAIClassifyResult(aiResponse, unclassified) {
  if (!Array.isArray(aiResponse)) return [];

  const results = [];
  for (const item of aiResponse) {
    const idx = item.index;
    if (idx !== undefined && idx >= 0 && idx < unclassified.length) {
      results.push({
        fileName: unclassified[idx].fileName,
        filePath: unclassified[idx].filePath,
        type: item.type || 'OTHER',
        code: item.code || '',
        description: item.description || '',
      });
    }
  }
  return results;
}

/**
 * 统计文件分类结果
 */
export function summarizeScan(models) {
  const summary = { total: models.length };
  const typeCounts = {};
  for (const m of models) {
    const type = m.type || 'OTHER';
    typeCounts[type] = (typeCounts[type] || 0) + 1;
  }
  return { ...summary, ...typeCounts };
}

/**
 * 向 AI 提交批量分类
 */
export async function classifyByAI(unclassified, projectTypeKey, $ai, disclosure = 'COMPACT') {
  if (unclassified.length === 0) return [];
  const prompt = buildAIClassifyPrompt(unclassified, projectTypeKey, disclosure);

  const endpoint = ($ai && $ai.endpoint) || '/data/v1/chat/completions';
  const model = ($ai && $ai.model) || 'ernie-4.0';

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model,
        messages: [{ role: 'user', content: prompt }],
        stream: false,
        temperature: 0.2,
      }),
    });

    const data = await response.json();
    let content;
    if (data.choices && data.choices[0]) {
      content = data.choices[0].message
        ? data.choices[0].message.content
        : data.choices[0].content;
    } else if (data.result) {
      content = data.result;
    } else {
      return [];
    }

    content = content.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/i, '').trim();
    const parsed = JSON.parse(content);
    return parseAIClassifyResult(parsed, unclassified);
  } catch (e) {
    console.error('AI 分类失败:', e);
    return [];
  }
}

export default {
  scanSourceFiles,
  classifyByRules,
  buildAIClassifyPrompt,
  parseAIClassifyResult,
  classifyByAI,
  summarizeScan,
  getClassifierConfig,
};
